import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ChefHat, Heart, Search, Zap } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { SkeletonLoader, EmptyState } from "@/components/system";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";

type Recipe = {
  id: string;
  title: string;
  cuisine: string | null;
  meal_type: string[] | null;
  dietary_tags: string[] | null;
  cover_image_url: string | null;
  computed_kcal_per_serving: number | null;
  computed_protein_g: number | null;
  rating: number | null;
  favorite: boolean;
  cooked_count: number;
  servings: number;
  prep_minutes: number | null;
  cook_minutes: number | null;
  workout_phase: string | null;
  training_phase_fit: string[];
};

type ActiveProgram = {
  id: string;
  program_template_id: string;
  start_date: string;
  end_date: string;
};

const WORKOUT_PHASE_LABELS: Record<string, string> = {
  pre_workout: "Pre-Workout",
  post_workout: "Post-Workout",
  recovery: "Recovery",
  everyday: "Everyday",
  race_day: "Race Day",
};

const KCAL_RANGES: Array<{ label: string; min: number; max: number }> = [
  { label: "<200", min: 0, max: 200 },
  { label: "200-300", min: 200, max: 300 },
  { label: "300-400", min: 300, max: 400 },
  { label: "400-500", min: 400, max: 500 },
  { label: "500-600", min: 500, max: 600 },
  { label: ">600", min: 600, max: Infinity },
];

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "5px 12px",
        borderRadius: "var(--radius-chip)",
        border: "1px solid var(--hair-2)",
        background: active ? "var(--accent-wash)" : "transparent",
        color: active ? "var(--accent)" : "var(--fg-2)",
        fontSize: "var(--type-meta)",
        cursor: "pointer",
        fontWeight: active ? 500 : 400,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

export function Recipes() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const profile = useAthleteProfile();
  const [q, setQ] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [workoutPhase, setWorkoutPhase] = useState<string | null>(null);
  const [kcalRange, setKcalRange] = useState<string | null>(null);

  const recipes = useQuery({
    queryKey: ["recipes", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("recipe")
        .select("id, title, cuisine, meal_type, dietary_tags, cover_image_url, computed_kcal_per_serving, computed_protein_g, rating, favorite, cooked_count, servings, prep_minutes, cook_minutes, workout_phase, training_phase_fit")
        .eq("user_id", userId!)
        .order("favorite", { ascending: false })
        .order("last_cooked_at", { ascending: false, nullsFirst: false })
        .order("title");
      if (error) throw error;
      return (data ?? []) as Recipe[];
    },
  });

  const activeProgram = useQuery({
    queryKey: ["active_program", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async (): Promise<ActiveProgram | null> => {
      if (!userId) return null;
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("user_program_activation" as never)
        .select("id, program_template_id, start_date, end_date")
        .eq("user_id", userId)
        .eq("state", "active")
        .lte("start_date", today)
        .gte("end_date", today)
        .maybeSingle();
      if (error) return null;
      return (data ?? null) as unknown as ActiveProgram | null;
    },
  });

  const dietPattern = profile.data?.diet_pattern ?? null;

  const allRecipes = recipes.data ?? [];

  const filtered = allRecipes.filter((r) => {
    if (favOnly && !r.favorite) return false;
    if (q && !r.title.toLowerCase().includes(q.toLowerCase())) return false;
    if (workoutPhase && r.workout_phase !== workoutPhase) return false;
    if (kcalRange) {
      const range = KCAL_RANGES.find((kr) => kr.label === kcalRange);
      if (range && r.computed_kcal_per_serving != null) {
        if (r.computed_kcal_per_serving < range.min || r.computed_kcal_per_serving >= range.max) return false;
      }
    }
    return true;
  });

  const whatsHot = activeProgram.data
    ? allRecipes.filter(
        (r) =>
          r.training_phase_fit?.length > 0 &&
          r.cooked_count === 0,
      ).slice(0, 4)
    : [];

  const tryNext = activeProgram.data
    ? allRecipes.filter((r) => r.cooked_count === 0).slice(0, 6)
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <ChefHat size={22} color="var(--accent)" />
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 28 }}>Recipes</h1>
        <div style={{ flex: 1 }} />
        <Link
          to="/recipes/new"
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            padding: "10px 16px",
            borderRadius: 10,
            fontSize: "var(--type-body-s)",
            textDecoration: "none",
          }}
        >
          + New recipe
        </Link>
      </header>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--fg-3)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search recipes…"
            style={{
              width: "100%",
              padding: "10px 12px 10px 32px",
              borderRadius: 8,
              border: "1px solid var(--hair-2)",
              background: "var(--bg)",
              color: "var(--fg)",
              fontSize: "var(--type-body-s)",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>
        <Chip label="Favorites" active={favOnly} onClick={() => setFavOnly((v) => !v)} />
        {dietPattern && (
          <span
            style={{
              fontSize: "var(--type-meta)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "var(--accent)",
              background: "var(--accent-wash)",
              padding: "3px 10px",
              borderRadius: "var(--radius-chip)",
              border: "1px solid var(--accent-dim)",
            }}
            title="Auto-applied from your profile"
          >
            {dietPattern.replace(/_/g, " ")}
          </span>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)", alignSelf: "center", marginRight: 4 }}>Phase</span>
        {Object.entries(WORKOUT_PHASE_LABELS).map(([val, label]) => (
          <Chip
            key={val}
            label={label}
            active={workoutPhase === val}
            onClick={() => setWorkoutPhase(workoutPhase === val ? null : val)}
          />
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)", alignSelf: "center", marginRight: 4 }}>Kcal</span>
        {KCAL_RANGES.map((kr) => (
          <Chip
            key={kr.label}
            label={kr.label}
            active={kcalRange === kr.label}
            onClick={() => setKcalRange(kcalRange === kr.label ? null : kr.label)}
          />
        ))}
      </div>

      {activeProgram.data && whatsHot.length > 0 && (
        <section>
          <header
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              marginBottom: "var(--space-3)",
            }}
          >
            <Zap size={14} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <span
              style={{
                fontSize: "var(--type-micro)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--fg-3)",
              }}
            >
              What's hot — from your active program
            </span>
          </header>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "var(--space-2)" }}>
            {whatsHot.map((r) => (
              <RecipeCard key={r.id} recipe={r} />
            ))}
          </div>
        </section>
      )}

      {activeProgram.data && tryNext.length > 0 && (
        <section>
          <header style={{ marginBottom: "var(--space-3)" }}>
            <span
              style={{
                fontSize: "var(--type-micro)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--fg-3)",
              }}
            >
              Try next
            </span>
          </header>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "var(--space-2)" }}>
            {tryNext.map((r) => (
              <RecipeCard key={r.id} recipe={r} />
            ))}
          </div>
        </section>
      )}

      {recipes.isLoading ? (
        <SkeletonLoader variant="card" height={300} />
      ) : filtered.length === 0 ? (
        <EmptyState
          headline="No recipes."
          sub="Add a recipe by URL, photo, or manually."
          cta={
            <Link to="/recipes/new" style={{ color: "var(--accent)", fontSize: "var(--type-body-s)" }}>
              Create your first recipe
            </Link>
          }
        />
      ) : (
        <section>
          <header style={{ marginBottom: "var(--space-3)" }}>
            <span style={{ fontSize: "var(--type-micro)", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--fg-3)" }}>
              All recipes ({filtered.length})
            </span>
          </header>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "var(--space-3)" }}>
            {filtered.map((r) => (
              <RecipeCard key={r.id} recipe={r} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function RecipeCard({ recipe: r }: { recipe: Recipe }) {
  return (
    <Link
      to={`/recipes/${r.id}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "var(--space-3)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        background: "var(--bg-2)",
        color: "var(--fg)",
        textDecoration: "none",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontWeight: 500, lineHeight: 1.2 }}>{r.title}</strong>
        {r.favorite && <Heart size={14} fill="currentColor" color="var(--accent)" />}
      </header>
      {r.workout_phase && (
        <span
          style={{
            alignSelf: "flex-start",
            fontSize: "var(--type-micro)",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: r.workout_phase === "pre_workout" ? "var(--accent)" : r.workout_phase === "post_workout" ? "var(--amber)" : "var(--fg-3)",
            background: "var(--bg-3)",
            padding: "1px 6px",
            borderRadius: "var(--radius-chip)",
          }}
        >
          {WORKOUT_PHASE_LABELS[r.workout_phase] ?? r.workout_phase}
        </span>
      )}
      <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
        {r.cuisine ?? "—"} · {(r.meal_type ?? []).join("/") || "—"}
      </span>
      {r.computed_kcal_per_serving != null && (
        <span className="mono" style={{ fontSize: 12 }}>
          {Math.round(r.computed_kcal_per_serving)} kcal · {Math.round(r.computed_protein_g ?? 0)}P · {r.servings} serv
        </span>
      )}
      {(r.prep_minutes != null || r.cook_minutes != null) && (
        <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
          {(r.prep_minutes ?? 0) + (r.cook_minutes ?? 0)} min
        </span>
      )}
      {r.cooked_count > 0 && (
        <span style={{ fontSize: 11, color: "var(--fg-3)" }}>cooked {r.cooked_count}x</span>
      )}
    </Link>
  );
}
